import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindValue, html, list } from "../lib/bind";
import { bindCheckbox, bindSelect, setSelectOptions } from "../lib/formBind";
import { form } from "../lib/form";
import { currentScope } from "../lib/scope";
import { formatMoney } from "../lib/money";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { breadcrumb } from "../stores/breadcrumb";
import { buildCategoryRulesSubNav } from "./category-rules-subnav";
import {
  type ApplyRulesResult,
  type Category,
  type CategoryKind,
  type CategoryRule,
  type MatchType,
  type RuleDirection,
  type TaxGroup,
  applyCategoryRules,
  createCategory,
  createCategoryRule,
  deleteCategory,
  deleteCategoryRule,
  getCategory,
  getCategoryRule,
  listCategories,
  listCategoryRules,
  updateCategory,
  updateCategoryRule,
} from "../services/category";
import { type LoanSummary, listLoans, loanOptionLabel } from "../services/loan";

const TAX_GROUP_LABELS: Record<TaxGroup, string> = {
  turnover: "Turnover",
  cost_of_goods: "Cost of goods",
  staff: "Staff",
  premises: "Premises",
  office_admin: "Office / admin (use of home)",
  travel: "Travel",
  motor: "Motor",
  professional_fees: "Professional fees",
  bank_interest_charges: "Bank charges / interest",
  loan_interest: "Loan interest",
  loan_principal: "Loan principal repayment",
  loan_received: "Loan drawdown received",
  equipment_capital: "Equipment / capital",
  marketing: "Marketing",
  insurance: "Insurance",
  subsistence: "Subsistence",
  training: "Training",
  software_subscriptions: "Software & subscriptions",
  other: "Other",
  non_allowable: "Non-allowable",
  vat_payment: "VAT payment",
  tax_payment: "Corporation Tax payment",
  owner_drawings: "Owner drawings",
};

const TAX_GROUP_OPTIONS = (Object.keys(TAX_GROUP_LABELS) as TaxGroup[]).map((value) => ({
  value,
  label: TAX_GROUP_LABELS[value],
}));

function emptyCategoryInput() {
  return {
    name: "",
    kind: "expense" as CategoryKind,
    tax_group: "other" as TaxGroup,
    allowable_for_tax: true,
  };
}

function emptyRuleInput() {
  return {
    priority: "100",
    match_type: "contains" as MatchType,
    pattern: "",
    amount_min: "",
    amount_max: "",
    category_id: "",
    loan_id: "",
    direction: "any" as RuleDirection,
    set_is_transfer: false,
    set_is_personal: false,
  };
}

export function categoriesListView(): Node {
  const categories = signal<Category[]>([]);
  const loadError = signal("");

  function reloadCategories(): void {
    listCategories()
      .then((result) => {
        categories.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reloadCategories();

  async function doDeleteCategory(category: Category): Promise<void> {
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    try {
      await deleteCategory(category.id);
      reloadCategories();
      toast.success("Category deleted.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card">
      <div class="card-label-row">
        <a href="/categories/new" data-link class="btn btn-primary">Add category</a>
      </div>
      <div class="data-table categories-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Tax group</th>
              <th>Allowable</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="categories-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("categories view produced no .view element");
  viewEl.insertBefore(buildCategoryRulesSubNav("categories"), viewEl.firstChild);

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const categoriesTable = fragment.querySelector<HTMLTableElement>(".categories-table table");
  const categoriesBody = fragment.querySelector<HTMLTableSectionElement>(".categories-body");

  if (!errorEl || !categoriesTable || !categoriesBody) {
    throw new Error("categories view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);

  list(
    categoriesBody,
    categories,
    (c) => c.id,
    (c) => {
      const row = html`<tr>
        <td><a href="/categories/${c.id}" data-link>${c.name}</a></td>
        <td>${c.kind}</td>
        <td>${TAX_GROUP_LABELS[c.tax_group]}</td>
        <td>${c.allowable_for_tax ? "Yes" : "No"}</td>
        <td><button class="btn btn-danger cat-delete-btn" type="button">Delete</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("category row template produced no element");
      const deleteBtn = row.querySelector<HTMLButtonElement>(".cat-delete-btn");
      deleteBtn?.addEventListener("click", () => {
        void doDeleteCategory(c);
      });
      return labelRow(row, categoriesTable);
    },
  );

  return fragment;
}

export function categoryRulesListView(): Node {
  const rules = signal<CategoryRule[]>([]);
  const loadError = signal("");

  function reloadRules(): void {
    listCategoryRules()
      .then((result) => {
        rules.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reloadRules();

  async function doDeleteRule(rule: CategoryRule): Promise<void> {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await deleteCategoryRule(rule.id);
      reloadRules();
      toast.success("Rule deleted.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  const applying = signal(false);
  const dryRunResult = signal<ApplyRulesResult | null>(null);

  async function runDryRun(): Promise<void> {
    applying.value = true;
    try {
      dryRunResult.value = await applyCategoryRules(true);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Dry run failed");
    } finally {
      applying.value = false;
    }
  }

  async function applyForReal(): Promise<void> {
    applying.value = true;
    try {
      const result = await applyCategoryRules(false);
      dryRunResult.value = null;
      toast.success(`${String(result.changes.length)} transaction(s) categorised.`);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Apply failed");
    } finally {
      applying.value = false;
    }
  }

  const hasDryRun = computed(() => dryRunResult.value !== null);

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card">
      <div class="card-label-row">
        <div class="card-label">Category rules</div>
        <a href="/category-rules/new" data-link class="btn btn-primary">Add rule</a>
      </div>
      <p class="field-hint">
        Applied on import, in priority order (lowest first) — first match wins.
      </p>
      <div class="data-table rules-table">
        <table>
          <thead>
            <tr>
              <th>Priority</th>
              <th>Match</th>
              <th>Pattern</th>
              <th>Direction</th>
              <th>Category</th>
              <th>Flags</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="rules-body"></tbody>
        </table>
      </div>
      <div class="form-actions apply-rules-actions">
        <button class="btn dry-run-btn" type="button">Dry run</button>
        <button class="btn btn-primary apply-btn" type="button">Apply for real</button>
      </div>
      <div class="data-table dry-run-results">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Would set</th>
            </tr>
          </thead>
          <tbody class="dry-run-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("category rules view produced no .view element");
  viewEl.insertBefore(buildCategoryRulesSubNav("rules"), viewEl.firstChild);

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const rulesTable = fragment.querySelector<HTMLTableElement>(".rules-table table");
  const rulesBody = fragment.querySelector<HTMLTableSectionElement>(".rules-body");
  const dryRunBtn = fragment.querySelector<HTMLButtonElement>(".dry-run-btn");
  const applyBtn = fragment.querySelector<HTMLButtonElement>(".apply-btn");
  const dryRunResults = fragment.querySelector<HTMLElement>(".dry-run-results");
  const dryRunTable = fragment.querySelector<HTMLTableElement>(".dry-run-results table");
  const dryRunBody = fragment.querySelector<HTMLTableSectionElement>(".dry-run-body");

  if (
    !errorEl ||
    !rulesTable ||
    !rulesBody ||
    !dryRunBtn ||
    !applyBtn ||
    !dryRunResults ||
    !dryRunTable ||
    !dryRunBody
  ) {
    throw new Error("category rules view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);

  dryRunBtn.addEventListener("click", () => {
    void runDryRun();
  });
  applyBtn.addEventListener("click", () => {
    void applyForReal();
  });
  bindShow(dryRunResults, hasDryRun);

  list(
    dryRunBody,
    computed(() => dryRunResult.value?.changes ?? []),
    (c) => c.transaction_id,
    (c) => {
      const flags: string[] = [];
      if (c.category_name) flags.push(c.category_name);
      if (c.loan_name) flags.push(`loan: ${c.loan_name}`);
      if (c.set_is_transfer) flags.push("transfer");
      if (c.set_is_personal) flags.push("personal");
      const row = html`<tr>
        <td>${c.date}</td>
        <td>${c.description}</td>
        <td>${formatMoney(c.amount)}</td>
        <td>${flags.join(", ")}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("dry-run row template produced no element");
      return labelRow(row, dryRunTable);
    },
  );

  list(
    rulesBody,
    rules,
    (r) => r.id,
    (r) => {
      const flags = [r.set_is_transfer ? "transfer" : "", r.set_is_personal ? "personal" : ""]
        .filter(Boolean)
        .join(", ");
      const row = html`<tr>
        <td>${String(r.priority)}</td>
        <td>${r.match_type}</td>
        <td><a href="/category-rules/${r.id}" data-link>${r.pattern}</a></td>
        <td>${r.direction}</td>
        <td>${r.category_name ?? "—"}</td>
        <td>${flags || "—"}</td>
        <td><button class="btn btn-danger rule-delete-btn" type="button">Delete</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("rule row template produced no element");
      const deleteBtn = row.querySelector<HTMLButtonElement>(".rule-delete-btn");
      deleteBtn?.addEventListener("click", () => {
        void doDeleteRule(r);
      });
      return labelRow(row, rulesTable);
    },
  );

  return fragment;
}

export function categoryDetailView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const categoryId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");

  breadcrumb.value = { label: "Expense categories", path: "/categories" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  const categoryForm = form(emptyCategoryInput());

  if (!isNew) {
    getCategory(paramId)
      .then((category) => {
        categoryForm.reset({
          name: category.name,
          kind: category.kind,
          tax_group: category.tax_group,
          allowable_for_tax: category.allowable_for_tax,
        });
        savedAt.value = category.updated_at;
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    if (!categoryForm.fields.name.value.trim()) {
      errorMessage.value = "Enter a category name.";
      return;
    }
    try {
      if (isNew) {
        const created = await createCategory(categoryForm.values());
        toast.queueSuccess("Category created.");
        location.assign(`/categories/${created.id}`);
        return;
      }
      const updated = await updateCategory(categoryId.value, {
        ...categoryForm.values(),
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      toast.success("Category saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This record changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function doDelete(): Promise<void> {
    if (!window.confirm("Delete this category?")) return;
    try {
      await deleteCategory(categoryId.value);
      toast.queueSuccess("Category deleted.");
      location.assign("/categories");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  const fragment = html`<div class="view">
    <div class="page-header"><h1>${isNew ? "New category" : "Edit category"}</h1></div>
    <p class="form-error"></p>

    <form class="form category-form" onsubmit="${submit}">
      <div class="form-row">
        <div class="form-field">
          <label for="cat-name">Name</label>
          <input id="cat-name" required />
        </div>
        <div class="form-field">
          <label for="cat-kind">Kind</label>
          <select id="cat-kind">
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
        <div class="form-field">
          <label for="cat-tax-group">Tax group</label>
          <select id="cat-tax-group"></select>
        </div>
      </div>
      <div class="checkbox-field">
        <input id="cat-allowable" type="checkbox" />
        <label for="cat-allowable">Allowable for tax</label>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
        <button class="btn btn-danger delete-btn" type="button">Delete</button>
      </div>
    </form>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const nameInput = fragment.querySelector<HTMLInputElement>("#cat-name");
  const kindSelect = fragment.querySelector<HTMLSelectElement>("#cat-kind");
  const taxGroupSelect = fragment.querySelector<HTMLSelectElement>("#cat-tax-group");
  const allowableInput = fragment.querySelector<HTMLInputElement>("#cat-allowable");
  const deleteBtn = fragment.querySelector<HTMLButtonElement>(".delete-btn");

  if (!errorEl || !nameInput || !kindSelect || !taxGroupSelect || !allowableInput || !deleteBtn) {
    throw new Error("category detail markup missing expected elements");
  }
  if (isNew) deleteBtn.style.display = "none";

  bindErrorText(errorEl, errorMessage);
  bindValue(nameInput, categoryForm.fields.name);
  bindSelect(kindSelect, categoryForm.fields.kind);
  setSelectOptions(taxGroupSelect, TAX_GROUP_OPTIONS);
  bindSelect(taxGroupSelect, categoryForm.fields.tax_group);
  bindCheckbox(allowableInput, categoryForm.fields.allowable_for_tax);
  deleteBtn.addEventListener("click", () => {
    void doDelete();
  });

  return fragment;
}

export function categoryRuleDetailView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const ruleId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");
  const categories = signal<Category[]>([]);
  const loans = signal<LoanSummary[]>([]);

  breadcrumb.value = { label: "Category rules", path: "/category-rules" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  listCategories()
    .then((result) => {
      categories.value = result;
    })
    .catch(() => undefined);
  listLoans()
    .then((result) => {
      loans.value = result;
    })
    .catch(() => undefined);

  const ruleForm = form(emptyRuleInput());

  if (!isNew) {
    getCategoryRule(paramId)
      .then((rule) => {
        ruleForm.reset({
          priority: String(rule.priority),
          match_type: rule.match_type,
          pattern: rule.pattern,
          amount_min: rule.amount_min ?? "",
          amount_max: rule.amount_max ?? "",
          category_id: rule.category_id ?? "",
          loan_id: rule.loan_id ?? "",
          direction: rule.direction,
          set_is_transfer: rule.set_is_transfer,
          set_is_personal: rule.set_is_personal,
        });
        savedAt.value = rule.updated_at;
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    if (!ruleForm.fields.pattern.value.trim()) {
      errorMessage.value = "Enter a match pattern.";
      return;
    }
    if (
      !ruleForm.fields.category_id.value &&
      !ruleForm.fields.loan_id.value &&
      !ruleForm.fields.set_is_transfer.value &&
      !ruleForm.fields.set_is_personal.value
    ) {
      errorMessage.value = "Pick a category or a loan, or flag as transfer/personal.";
      return;
    }
    const input = {
      priority: Number(ruleForm.fields.priority.value) || 100,
      match_type: ruleForm.fields.match_type.value,
      pattern: ruleForm.fields.pattern.value,
      amount_min: ruleForm.fields.amount_min.value || null,
      amount_max: ruleForm.fields.amount_max.value || null,
      category_id: ruleForm.fields.category_id.value || null,
      loan_id: ruleForm.fields.loan_id.value || null,
      direction: ruleForm.fields.direction.value,
      set_is_transfer: ruleForm.fields.set_is_transfer.value,
      set_is_personal: ruleForm.fields.set_is_personal.value,
    };
    try {
      if (isNew) {
        const created = await createCategoryRule(input);
        toast.queueSuccess("Rule created.");
        location.assign(`/category-rules/${created.id}`);
        return;
      }
      const updated = await updateCategoryRule(ruleId.value, {
        ...input,
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      toast.success("Rule saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This record changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function doDelete(): Promise<void> {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await deleteCategoryRule(ruleId.value);
      toast.queueSuccess("Rule deleted.");
      location.assign("/category-rules");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  const fragment = html`<div class="view">
    <div class="page-header"><h1>${isNew ? "New rule" : "Edit rule"}</h1></div>
    <p class="form-error"></p>

    <form class="form rule-form" onsubmit="${submit}">
      <div class="form-row">
        <div class="form-field">
          <label for="rule-priority">Priority</label>
          <input id="rule-priority" inputmode="numeric" />
        </div>
        <div class="form-field">
          <label for="rule-match-type">Match</label>
          <select id="rule-match-type">
            <option value="contains">Contains</option>
            <option value="starts_with">Starts with</option>
            <option value="regex">Regex</option>
          </select>
        </div>
        <div class="form-field">
          <label for="rule-pattern">Pattern</label>
          <input id="rule-pattern" required />
        </div>
        <div class="form-field">
          <label for="rule-direction">Direction</label>
          <select id="rule-direction">
            <option value="any">Any</option>
            <option value="in">Money in</option>
            <option value="out">Money out</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="rule-amount-min">Amount min</label>
          <input id="rule-amount-min" inputmode="decimal" placeholder="optional" />
        </div>
        <div class="form-field">
          <label for="rule-amount-max">Amount max</label>
          <input id="rule-amount-max" inputmode="decimal" placeholder="optional" />
          <p class="field-hint">Matches the transaction's unsigned amount, when set.</p>
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="rule-category">Category</label>
          <select id="rule-category"><option value="">(none)</option></select>
        </div>
        <div class="form-field">
          <label for="rule-loan">Loan</label>
          <select id="rule-loan"><option value="">(none)</option></select>
          <p class="field-hint">
            Links matched payments to this loan. If the loan has a repayment schedule
            loaded, they are split into interest and principal automatically.
          </p>
        </div>
        <div class="checkbox-field">
          <input id="rule-transfer" type="checkbox" />
          <label for="rule-transfer">Flag transfer</label>
        </div>
        <div class="checkbox-field">
          <input id="rule-personal" type="checkbox" />
          <label for="rule-personal">Flag personal</label>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
        <button class="btn btn-danger delete-btn" type="button">Delete</button>
      </div>
    </form>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const priorityInput = fragment.querySelector<HTMLInputElement>("#rule-priority");
  const matchTypeSelect = fragment.querySelector<HTMLSelectElement>("#rule-match-type");
  const patternInput = fragment.querySelector<HTMLInputElement>("#rule-pattern");
  const directionSelect = fragment.querySelector<HTMLSelectElement>("#rule-direction");
  const amountMinInput = fragment.querySelector<HTMLInputElement>("#rule-amount-min");
  const amountMaxInput = fragment.querySelector<HTMLInputElement>("#rule-amount-max");
  const categorySelect = fragment.querySelector<HTMLSelectElement>("#rule-category");
  const loanSelect = fragment.querySelector<HTMLSelectElement>("#rule-loan");
  const transferInput = fragment.querySelector<HTMLInputElement>("#rule-transfer");
  const personalInput = fragment.querySelector<HTMLInputElement>("#rule-personal");
  const deleteBtn = fragment.querySelector<HTMLButtonElement>(".delete-btn");

  if (
    !errorEl ||
    !priorityInput ||
    !matchTypeSelect ||
    !patternInput ||
    !directionSelect ||
    !amountMinInput ||
    !amountMaxInput ||
    !categorySelect ||
    !loanSelect ||
    !transferInput ||
    !personalInput ||
    !deleteBtn
  ) {
    throw new Error("rule detail markup missing expected elements");
  }
  if (isNew) deleteBtn.style.display = "none";

  bindErrorText(errorEl, errorMessage);
  bindValue(priorityInput, ruleForm.fields.priority);
  bindSelect(matchTypeSelect, ruleForm.fields.match_type);
  bindValue(patternInput, ruleForm.fields.pattern);
  bindSelect(directionSelect, ruleForm.fields.direction);
  bindValue(amountMinInput, ruleForm.fields.amount_min);
  bindValue(amountMaxInput, ruleForm.fields.amount_max);
  bindCheckbox(transferInput, ruleForm.fields.set_is_transfer);
  bindCheckbox(personalInput, ruleForm.fields.set_is_personal);

  currentScope().effect(() => {
    setSelectOptions(
      categorySelect,
      categories.value.map((c) => ({ value: c.id, label: c.name })),
      { placeholder: "(none)" },
    );
  });
  bindSelect(categorySelect, ruleForm.fields.category_id);

  currentScope().effect(() => {
    setSelectOptions(
      loanSelect,
      loans.value.map((l) => ({ value: l.id, label: loanOptionLabel(l) })),
      { placeholder: "(none)" },
    );
  });
  bindSelect(loanSelect, ruleForm.fields.loan_id);

  deleteBtn.addEventListener("click", () => {
    void doDelete();
  });

  return fragment;
}
